import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, MessageSquare, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

function timeLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR") + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export default function AdminMessages() {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // {user_id, user_name}
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef();

  const loadConvs = () => api.get("/admin/messages").then((r) => setConvs(r.data.conversations || [])).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    loadConvs();
    const t = setInterval(loadConvs, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const openConv = async (c) => {
    setActive({ user_id: c.user_id, user_name: c.user_name });
    setLoadingThread(true);
    try {
      const r = await api.get(`/admin/messages/${c.user_id}`);
      setThread(r.data.messages || []);
      loadConvs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoadingThread(false); }
  };

  const reply = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/messages/${active.user_id}`, { text: t });
      setThread((m) => [...m, r.data]);
      setText("");
      loadConvs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#00E676]" /></div>;

  // Vue conversation ouverte
  if (active) {
    return (
      <div className="max-w-2xl" data-testid="admin-messages-thread">
        <button onClick={() => { setActive(null); setThread([]); }} data-testid="admin-msg-back" className="flex items-center gap-2 text-sm text-[#39FF14] mb-4">
          <ArrowLeft size={16} /> Retour aux conversations
        </button>
        <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5">
          <h3 className="font-bold flex items-center gap-2 mb-3"><MessageSquare size={16} className="text-[#00E676]" /> {active.user_name}</h3>
          <div className="max-h-96 overflow-y-auto space-y-3 rounded-lg bg-[#0F1812] border border-[#00E676]/10 p-4">
            {loadingThread ? (
              <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#00E676]" /></div>
            ) : thread.length === 0 ? (
              <p className="text-center text-sm text-[#697C6F] py-6">Aucun message.</p>
            ) : (
              thread.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.sender === "admin" ? "bg-gradient-to-r from-[#00E676] to-[#39FF14] text-[#04180a]" : "bg-[#1A2B20] text-white border border-[#00E676]/15"}`}>
                    {m.category && m.sender === "user" && <div className="text-[10px] font-black uppercase text-[#39FF14] mb-0.5">{m.category}</div>}
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-[#04180a]/60" : "text-[#697C6F]"}`}>{timeLabel(m.created_at)}</div>
                  </div>
                </div>
              ))
            )}
            <div ref={endRef} />
          </div>
          <div className="mt-3 flex items-end gap-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); reply(); } }}
              placeholder="Votre réponse…"
              rows={2}
              data-testid="admin-msg-input"
              className="flex-1 bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14] resize-none"
            />
            <button onClick={reply} disabled={sending || !text.trim()} data-testid="admin-msg-send"
              className="px-4 py-3 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] disabled:opacity-50 flex items-center gap-2 shrink-0">
              {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Liste des conversations
  return (
    <div className="max-w-2xl space-y-3" data-testid="admin-messages-list">
      <h2 className="text-lg font-bold flex items-center gap-2"><MessageSquare size={18} className="text-[#00E676]" /> Messagerie des joueurs</h2>
      {convs.length === 0 ? (
        <p className="text-sm text-[#697C6F]">Aucun message reçu pour le moment.</p>
      ) : (
        convs.map((c) => (
          <button
            key={c.user_id}
            onClick={() => openConv(c)}
            data-testid={`admin-conv-${c.user_id}`}
            className="w-full text-left rounded-xl border border-[#00E676]/15 bg-[#131F17] p-4 hover:border-[#00E676]/40 transition-colors flex items-center gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="font-bold flex items-center gap-2">
                {c.user_name}
                {c.last_category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00E676]/15 text-[#39FF14] font-bold uppercase">{c.last_category}</span>}
              </div>
              <div className="text-xs text-[#697C6F] truncate mt-0.5">
                {c.last_sender === "admin" ? "Vous : " : ""}{c.last_text}
              </div>
              <div className="text-[10px] text-[#697C6F] mt-0.5">{c.last_at ? timeLabel(c.last_at) : ""}</div>
            </div>
            {c.unread > 0 && (
              <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-bold grid place-items-center shrink-0" data-testid={`conv-unread-${c.user_id}`}>
                {c.unread}
              </span>
            )}
          </button>
        ))
      )}
    </div>
  );
}
